import React from 'react';
import './ActivityMap.css';

/*
 * ActivityMap — nationwide rollout section (hub-and-spoke layout).
 *
 * Generic platform-aspect nodes only. No named cities, counties, or states.
 * No fake live activity. All class names prefixed `activity-map-*` for layout
 * isolation.
 *
 * Coordinates use a 100x100 normalized space matching the container
 * (the SVG uses preserveAspectRatio="none" so coords map 1:1 to CSS %).
 *
 * Tiers:
 *   core      — currently-active platform aspect (solid border)
 *   buildout  — growth/expansion aspect (dashed border, softer)
 */

const HUB = { x: 50, y: 50 };

const NODES = [
  // Top row — currently-active platform aspects (core)
  { key: 'n1', x: 18, y: 28, label: 'Contractor coverage',       tier: 'core',
    line: `M${HUB.x},${HUB.y} Q30,38 18,28` },
  { key: 'n2', x: 50, y: 12, label: 'Homeowner requests',        tier: 'core',
    line: `M${HUB.x},${HUB.y} L50,12` },
  { key: 'n3', x: 82, y: 28, label: 'Property manager requests', tier: 'core',
    line: `M${HUB.x},${HUB.y} Q70,38 82,28` },

  // Bottom row — growth / expansion aspects (buildout)
  { key: 'n4', x: 18, y: 78, label: 'Local service areas',       tier: 'buildout',
    line: `M${HUB.x},${HUB.y} Q30,62 18,78` },
  { key: 'n5', x: 50, y: 90, label: 'Market expansion',          tier: 'buildout',
    line: `M${HUB.x},${HUB.y} L50,90` },
  { key: 'n6', x: 82, y: 78, label: 'Coverage building',         tier: 'buildout',
    line: `M${HUB.x},${HUB.y} Q70,62 82,78` },
];

const PANEL_ITEMS = [
  {
    title: 'Nationwide platform',
    body: 'Homeowners and property managers can post repair requests through RealTimeRepairs.',
  },
  {
    title: 'Market-by-market rollout',
    body: 'Contractor coverage is being built market by market as local pros join.',
  },
  {
    title: 'Contractor network growing',
    body: 'Contractors can join early and get notified when requests match their trade and service area.',
  },
  {
    title: 'Direct customer-to-contractor model',
    body: 'No escrow, no per-lead fees, and customers pay contractors directly.',
  },
];

const ActivityMap = () => (
  <section className="activity-map-section">
    <div className="activity-map-inner">
      <div className="activity-map-header">
        <span className="activity-map-eyebrow">NATIONAL ROLLOUT</span>
        <h2>A Repair Marketplace Expanding Market by Market</h2>
        <p>RealTimeRepairs is designed to help homeowners and property managers post repair requests across the U.S. We're building contractor coverage market by market, starting with focused rollout areas.</p>
      </div>

      <div className="activity-map-content">
        <div className="activity-map-network">
          <svg
            className="activity-map-network-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {NODES.map((n) => (
              <path
                key={n.key}
                d={n.line}
                className={`activity-map-line activity-map-line-${n.tier}`}
                fill="none"
              />
            ))}
          </svg>

          {/* Central hub */}
          <div
            className="activity-map-hub"
            style={{ left: `${HUB.x}%`, top: `${HUB.y}%` }}
          >
            <span className="activity-map-hub-eyebrow">PLATFORM</span>
            <span className="activity-map-hub-label">RealTimeRepairs</span>
            <span className="activity-map-hub-sub">Built nationwide, rolled out locally</span>
          </div>

          {/* Generic platform-aspect nodes */}
          {NODES.map((n) => (
            <div
              key={n.key}
              className={`activity-map-node activity-map-node-${n.tier}`}
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            >
              <span className="activity-map-node-pin" aria-hidden="true"></span>
              <span className="activity-map-node-label">{n.label}</span>
            </div>
          ))}
        </div>

        <div className="activity-map-panel">
          {PANEL_ITEMS.map((item, i) => (
            <div key={i} className="activity-map-panel-item">
              <span className="activity-map-panel-bullet" aria-hidden="true"></span>
              <div className="activity-map-panel-content">
                <h3 className="activity-map-panel-title">{item.title}</h3>
                <p className="activity-map-panel-body">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="activity-map-footnote">
        Early focus areas are handled by the team during rollout, but the platform is built for repair requests across the U.S.
      </p>
    </div>
  </section>
);

export default ActivityMap;
